//
// # Wire Geometry Invariants
//
// The properties schematic wire geometry is supposed to hold after any edit.
// `Wires.add` maintains them for wires inserted through its merge/split path,
// but modes which build geometry themselves (drag, stretch, segment-slide) can
// and do violate them. Checking is centralized here so tests and dev-time
// assertions agree on what "valid" means.
//
// Deliberately structural: this takes anything with `start()`/`end()` rather
// than a `WireSegment`, so it stays free of the content/schematic tree and can
// run against plain fixtures.
//

import { Point } from "GenAlphaModel";

// The slice of a wire segment these checks need.
export interface SegmentLike {
  start(): Point;
  end(): Point;
}

// Kinds of invariant violation, in the order we report them.
export type ViolationKind =
  // Both endpoints at the same coordinate. Renders as nothing, but still
  // contributes a wire to dot inference, producing spurious junction dots.
  | "zero-length"
  // Neither horizontal nor vertical. Unselectable (hit-testing runs through
  // `Manhattan.calc`, which returns null) and invisible to connectivity
  // extraction, so the net silently splits.
  | "non-manhattan"
  // Two collinear segments sharing more than a single point. Redundant
  // geometry: invisible on screen, but it perturbs dot inference and makes
  // clicks in the overlapped span ambiguous.
  | "collinear-overlap";

export interface Violation {
  kind: ViolationKind;
  // The offending segment(s): one for zero-length / non-manhattan, two for an
  // overlapping pair.
  segments: SegmentLike[];
  // Human-readable detail, for test failure messages.
  message: string;
}

// Format a segment as "(x0,y0)->(x1,y1)" for violation messages.
function fmt(seg: SegmentLike): string {
  const s = seg.start();
  const e = seg.end();
  return `(${s.x},${s.y})->(${e.x},${e.y})`;
}

// Does `seg` have both endpoints at the same point?
function isZeroLength(seg: SegmentLike): boolean {
  return Point.eq(seg.start(), seg.end());
}

// Is `seg` axis-aligned? Zero-length segments trivially satisfy this, so
// callers should test for zero-length first.
function isAxisAligned(seg: SegmentLike): boolean {
  const s = seg.start();
  const e = seg.end();
  return s.x === e.x || s.y === e.y;
}

// Two half-open extents overlap when they share more than a single point.
// Segments meeting end-to-end (a corner or butt joint) touch at exactly one
// coordinate and are legal, so the comparison is strict.
function extentsOverlap(
  aMin: number,
  aMax: number,
  bMin: number,
  bMax: number
): boolean {
  return Math.min(aMax, bMax) - Math.max(aMin, bMin) > 0;
}

export interface CheckOptions {
  // Suppress `zero-length` reporting.
  //
  // Nets are built purely from wire segments (see `ConnectedGroup` in the
  // circuit extractor): two instance pins at the same coordinate with no wire
  // between them extract as unconnected. So when a drag collapses a wire onto
  // a coincident pin, one zero-length segment is deliberately kept as the
  // abutment marker that holds the net together. Callers which permit that
  // marker set this; a zero-length segment is not otherwise distinguishable
  // from drag debris by geometry alone.
  //
  // Remove this once the extractor treats coincident pins as connected, at
  // which point collapsed wires can simply be deleted.
  allowAbutmentStubs?: boolean;
}

// Check every invariant over `segments`, returning all violations found.
// An empty result means the geometry is well-formed.
export function checkWireInvariants(
  segments: SegmentLike[],
  opts: CheckOptions = {}
): Violation[] {
  const violations: Violation[] = [];

  // Group axis-aligned segments for the overlap pass as we go: horizontals by
  // their y coordinate, verticals by their x.
  const horiz = new Map<number, SegmentLike[]>();
  const vert = new Map<number, SegmentLike[]>();

  for (const seg of segments) {
    if (isZeroLength(seg)) {
      if (!opts.allowAbutmentStubs) {
        violations.push({
          kind: "zero-length",
          segments: [seg],
          message: `zero-length segment at ${fmt(seg)}`,
        });
      }
      continue;
    }
    if (!isAxisAligned(seg)) {
      violations.push({
        kind: "non-manhattan",
        segments: [seg],
        message: `non-manhattan segment ${fmt(seg)}`,
      });
      continue;
    }
    const s = seg.start();
    const e = seg.end();
    const group = s.y === e.y ? horiz : vert;
    const key = s.y === e.y ? s.y : s.x;
    const bucket = group.get(key);
    if (bucket) bucket.push(seg);
    else group.set(key, [seg]);
  }

  // Collinear overlap: within a group every segment shares the fixed
  // coordinate, so only the varying extents need comparing.
  const checkGroup = (bucket: SegmentLike[], along: "x" | "y") => {
    const extent = (seg: SegmentLike): [number, number] => {
      const a = along === "x" ? seg.start().x : seg.start().y;
      const b = along === "x" ? seg.end().x : seg.end().y;
      return [Math.min(a, b), Math.max(a, b)];
    };
    for (let i = 0; i < bucket.length; i++) {
      for (let j = i + 1; j < bucket.length; j++) {
        const [aMin, aMax] = extent(bucket[i]);
        const [bMin, bMax] = extent(bucket[j]);
        if (!extentsOverlap(aMin, aMax, bMin, bMax)) continue;
        violations.push({
          kind: "collinear-overlap",
          segments: [bucket[i], bucket[j]],
          message: `collinear segments overlap: ${fmt(bucket[i])} and ${fmt(
            bucket[j]
          )}`,
        });
      }
    }
  };
  for (const bucket of horiz.values()) checkGroup(bucket, "x");
  for (const bucket of vert.values()) checkGroup(bucket, "y");

  return violations;
}

// Convenience for assertions and test messages: a single string describing
// every violation, or null when the geometry is clean.
export function describeWireViolations(
  segments: SegmentLike[],
  opts: CheckOptions = {}
): string | null {
  const violations = checkWireInvariants(segments, opts);
  if (violations.length === 0) return null;
  return violations.map((v) => `${v.kind}: ${v.message}`).join("\n");
}
